import {
  Plane,
  ArrowRightLeft,
  Globe,
  Award,
  MapPin,
  Truck,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { analyticsEvents, trackEvent } from "@/lib/ga4";

const heroSlides = [
  {
    id: "legacy",
    badge: "Celebrating 25 Years of Our Journey",
    title: "Trusted Forex & Travel Services",
    highlight: "Built on 25 Years of Experience",
    description:
      "Delivering reliable currency exchange and travel-focused forex solutions with transparency, convenience, and care.",
    ctaPrimary: {
      label: "Check Forex Rates",
      href: "/live-rates",
      analytics: "hero_check_forex_rates_15y",
    },
    ctaSecondary: {
      label: "Book Pickup / Delivery",
      href: "/contact",
      analytics: "hero_pickup_delivery_15y",
    },
    card: {
      label: "Our Legacy",
      value: "25",
      valueSuffix: "+",
      body: "Years of serving customers with trusted forex expertise, transparent pricing, and convenient pickup & delivery services.",
      stats: [
        { title: "All Currencies", subtitle: "Best Available Rates" },
        {
          title: "3 Locations",
          subtitle: "Vasant Kunj · Connaught Place (CP) · Sarita Vihar",
        },
      ],
    },
  },
  {
    id: "precision",
    badge: "Premium Forex for Modern Travelers",
    title: "Precision Rates, Seamless Service",
    highlight: "Designed for Confident Travel",
    description:
      "A refined forex experience with real-time pricing, tailored support, and dependable fulfillment.",
    ctaPrimary: {
      label: "View Live Rates",
      href: "/live-rates",
      analytics: "hero_view_live_rates",
    },
    ctaSecondary: {
      label: "Schedule a Pickup",
      href: "/contact",
      analytics: "hero_schedule_pickup",
    },
    card: {
      label: "Trusted Choice",
      value: "24",
      valueSuffix: "/7",
      body: "Responsive guidance with clear communication and secure handling from inquiry to delivery.",
      stats: [
        { title: "Best Rates", subtitle: "Transparent Pricing" },
        { title: "Dedicated Team", subtitle: "Travel-first Support" },
      ],
    },
  },
  {
    id: "concierge",
    badge: "Concierge-Level Forex Experience",
    title: "Professional, Secure, Discreet",
    highlight: "Forex That Matches Your Standards",
    description:
      "A polished, appointment-ready experience for business and leisure travelers alike.",
    ctaPrimary: {
      label: "Explore Services",
      href: "/live-rates",
      analytics: "hero_explore_services",
    },
    ctaSecondary: {
      label: "Contact Concierge",
      href: "/contact",
      analytics: "hero_contact_concierge",
    },
    card: {
      label: "Concierge Care",
      value: "100",
      valueSuffix: "%",
      body: "Discreet pickup and secure delivery backed by a reputation built on reliability.",
      stats: [
        { title: "Doorstep", subtitle: "Pickup & Delivery" },
        {
          title: "Coverage",
          subtitle: "Vasant Kunj · Connaught Place (CP) · Sarita Vihar",
        },
      ],
    },
  },
  {
    id: "travel-packages",
    badge: "Curated Travel Packages",
    title: "Discover Inspiring Journeys",
    highlight: "Handpicked Packages for Every Traveler",
    description:
      "Explore thoughtfully designed getaways with transparent pricing, expert planning, and end-to-end support.",
    ctaPrimary: {
      label: "View Travel Packages",
      href: "/packages",
      analytics: "hero_view_travel_packages",
    },
    ctaSecondary: {
      label: "Talk to a Travel Expert",
      href: "/contact",
      analytics: "hero_talk_travel_expert",
    },
    card: {
      label: "Featured Getaways",
      value: "50",
      valueSuffix: "+",
      body: "Domestic and international itineraries crafted for comfort, value, and unforgettable experiences.",
      stats: [
        { title: "Best Sellers", subtitle: "Family · Honeymoon · Adventure" },
        { title: "Support", subtitle: "Planning to Return Journey" },
      ],
    },
  },
];

export default function HeroSection() {
  /* =====================================================================
     PREVIOUS HERO SECTION (COMMENTED FOR REFERENCE)
     DO NOT DELETE – preserved intentionally
  ======================================================================

  (Entire previous hero code remains here, unchanged and commented)

  ====================================================================== */

  const [activeIndex, setActiveIndex] = useState(0);
  const activeSlide = heroSlides[activeIndex];

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % heroSlides.length);
    }, 9000);

    return () => window.clearInterval(intervalId);
  }, []);

  const recordCta = (cta_label: string, destination_url: string) => {
    trackEvent({
      name: analyticsEvents.ctaClick,
      params: {
        content_type: "cta",
        item_id: cta_label,
        item_name: cta_label,
        cta_label,
        destination_url,
        page_path: "/",
        page_type: "home",
      },
    });
  };

  return (
    <section
      id="home"
      className="relative overflow-hidden bg-gradient-to-br from-[#faf8f4] via-white to-[#f1efe9]"
    >
      {/* Elegant background accents */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[#c9a24d]/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-[#1f2a44]/20 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-[70vh] max-w-7xl items-center px-6 pt-14 pb-28 lg:min-h-[50vh] lg:px-8 lg:pt-8 lg:pb-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* LEFT CONTENT */}
          <div className="space-y-8 lg:space-y-4">
            {/* 25 Years Badge */}
            <div className="inline-flex items-center gap-3 rounded-full border border-[#c9a24d]/40 bg-white px-6 py-2 shadow-sm">
              <Award className="h-5 w-5 text-[#c9a24d]" />
              <span className="text-sm font-semibold tracking-wide text-gray-800">
                {activeSlide.badge}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl xl:text-6xl font-serif leading-tight text-gray-900">
              {activeSlide.title}
              <span className="block text-[#1f2a44]">
                {activeSlide.highlight}
              </span>
            </h1>

            <p className="text-lg text-gray-600 max-w-xl">
              {activeSlide.description}
            </p>

            {/* CORE REQUIREMENTS – CLEAR & VISIBLE */}
            <div className="grid sm:grid-cols-2 gap-5 pt-4 text-gray-700">
              <div className="flex items-start gap-3">
                <ArrowRightLeft className="h-5 w-5 text-[#c9a24d]" />
                <span>
                  <strong>Forex Update:</strong> Best rates available for all
                  currencies
                </span>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#c9a24d]" />
                <span>
                  <strong>Pickup Facility:</strong> Vasant Kunj, Connaught Place
                  (CP) & Sarita Vihar
                </span>
              </div>

              <div className="flex items-start gap-3">
                <Truck className="h-5 w-5 text-[#c9a24d]" />
                <span>
                  <strong>Delivery Service:</strong> Available (delivery charges
                  apply)
                </span>
              </div>

              <div className="flex items-start gap-3">
                <Globe className="h-5 w-5 text-[#c9a24d]" />
                <span>
                  Supporting travellers with dependable forex solutions
                </span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <Button
                asChild
                className="bg-[#1f2a44] hover:bg-[#162033] text-white px-10 py-5 text-lg font-semibold h-auto shadow-lg"
              >
                <Link
                  to={activeSlide.ctaPrimary.href}
                  onClick={() =>
                    recordCta(
                      activeSlide.ctaPrimary.analytics,
                      activeSlide.ctaPrimary.href,
                    )
                  }
                >
                  <ArrowRightLeft className="w-5 h-5 mr-2" />
                  {activeSlide.ctaPrimary.label}
                </Link>
              </Button>

              <Button
                asChild
                variant="outline"
                className="border-[#c9a24d] text-[#1f2a44] hover:bg-[#c9a24d]/10 px-10 py-5 text-lg font-semibold h-auto"
              >
                <Link
                  to={activeSlide.ctaSecondary.href}
                  onClick={() =>
                    recordCta(
                      activeSlide.ctaSecondary.analytics,
                      activeSlide.ctaSecondary.href,
                    )
                  }
                >
                  <Plane className="w-5 h-5 mr-2" />
                  {activeSlide.ctaSecondary.label}
                </Link>
              </Button>
            </div>
          </div>

          {/* RIGHT – ELEGANT VISUAL CARD */}
          <div className="relative">
            <div className="relative min-h-[320px] lg:min-h-[160px]">
              {heroSlides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`absolute inset-0 rounded-[36px] border border-white bg-white/85 p-10 shadow-[0_30px_90px_rgba(0,0,0,0.15)] backdrop-blur transition-all duration-700 ${
                    index === activeIndex
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-6 pointer-events-none"
                  }`}
                  aria-hidden={index !== activeIndex}
                >
                  <div className="relative h-full">
                    <img
                      src="/legacy1.png"
                      alt="Legacy badge"
                      width={64}
                      height={64}
                      className="absolute left-1/2 -top-10 h-16 w-16 -translate-x-1/2 object-contain"
                      loading="lazy"
                    />
                    <div className="space-y-6">
                      <div className="text-xs tracking-[0.35em] uppercase text-gray-400">
                        {slide.card.label}
                      </div>

                      <div className="text-6xl font-serif text-gray-900">
                        {slide.card.value}
                        <span className="text-[#c9a24d]">
                          {slide.card.valueSuffix}
                        </span>
                      </div>

                      <p className="text-gray-600">{slide.card.body}</p>

                      <div className="grid grid-cols-2 gap-4 pt-4">
                        {slide.card.stats.map((stat) => (
                          <div
                            key={stat.title}
                            className="rounded-2xl bg-[#faf8f4] p-4 text-center"
                          >
                            <div className="text-2xl font-semibold text-gray-900">
                              {stat.title}
                            </div>
                            <div className="text-xs text-gray-500">
                              {stat.subtitle}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-[#c9a24d]/30 blur-3xl pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-4 flex items-center justify-center gap-3 pointer-events-auto z-20">
        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#c9a24d]/40 bg-white text-[#1f2a44] shadow-sm transition hover:border-[#c9a24d]"
          aria-label="Previous slide"
          onClick={() =>
            setActiveIndex(
              (prev) => (prev - 1 + heroSlides.length) % heroSlides.length,
            )
          }
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-1 sm:gap-2">
          {heroSlides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c9a24d]"
              aria-label={`Go to slide ${index + 1}`}
              aria-pressed={index === activeIndex}
              onClick={() => setActiveIndex(index)}
            >
              <span
                className={`block h-2.5 w-2.5 rounded-full transition ${
                  index === activeIndex ? "bg-[#c9a24d]" : "bg-[#c9a24d]/30"
                }`}
                aria-hidden
              />
            </button>
          ))}
        </div>

        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#c9a24d]/40 bg-white text-[#1f2a44] shadow-sm transition hover:border-[#c9a24d]"
          aria-label="Next slide"
          onClick={() =>
            setActiveIndex((prev) => (prev + 1) % heroSlides.length)
          }
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </section>
  );
}
